import '../../pages/ScoreAthletePage.css';

import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';

import AthleteCardTop from '../../components/common/AthleteCardTop/AthleteCardTop';
import AthleteQuickSelectTabBar
  from '../../components/common/AthleteQuickSelectTabBar/AthleteQuickSelectTabBar';
import BackArrowIcon from '../../components/common/BackButton/BackArrowIcon';
import Button from '../../components/common/Button';
import FullscreenPhotoViewer
  from '../../components/common/FullscreenPhotoViewer/FullscreenPhotoViewer';
import PerCoachCommentsSection
  from '../../components/common/PerCoachCommentsSection/PerCoachCommentsSection';
import PageTopNav from '../../components/common/PageTopNav/PageTopNav';
import SectionLauncherButton
  from '../../components/common/SectionLauncherButton/SectionLauncherButton';
import {
  parsePerCoachComments,
  parseSkillFieldValue,
} from '../../services/athleteService';
import { SkillValue } from '../../types/athlete';
import { generateCommentId } from '../../utils/ids';
import {
  FormField,
  FormGroup,
  GENERAL_COMMENTS_FIELD_ID,
  GENERAL_COMMENTS_GROUP_ID,
} from '../../types/formSchema';
import DemoSkillField from '../components/DemoSkillField';
import { useDemoStore } from '../store/DemoStoreContext';

const MOBILE_CARD_COMPACT_MEDIA_QUERY = '(max-width: 768px)';


const DemoScoreAthletePage: React.FC = () => {
  const { athleteId } = useParams<{ athleteId: string }>();
  const navigate = useNavigate();
  const {
    tryoutMeta,
    coach,
    athletes,
    tabbedAthletes,
    setScoreField,
    addGeneralComment,
    editGeneralComment,
    deleteGeneralComment,
    setTabbedAthletes,
  } = useDemoStore();

  const [activeGroupId, setActiveGroupId] = useState('');
  const [cardCompact, setCardCompact] = useState(false);
  const [cardViewerOpen, setCardViewerOpen] = useState(false);
  const scrollRegionRef = useRef<HTMLDivElement>(null);
  const stickyCardRef = useRef<HTMLElement>(null);

  const currentAthlete = useMemo(
    () => athletes.find((a) => a.id === athleteId) ?? null,
    [athletes, athleteId],
  );

  const groups = useMemo(
    () => [...tryoutMeta.formSchema.groups].sort((a, b) => a.order - b.order),
    [tryoutMeta.formSchema.groups],
  );

  const quickAccessAthletes = useMemo(() => {
    if (tabbedAthletes.length === 0) return [];
    const byId = new Map(athletes.map((a) => [a.id, a]));
    return tabbedAthletes
      .map((id) => byId.get(id))
      .filter((a): a is typeof athletes[number] => Boolean(a));
  }, [tabbedAthletes, athletes]);

  const inQuickAccess = !!athleteId && tabbedAthletes.includes(athleteId);
  const hasQuickAccessHotbar = quickAccessAthletes.length > 0;

  // Sticky card height observer (mirrors real page).
  useEffect(() => {
    const scrollRegion = scrollRegionRef.current;
    const stickyCard = stickyCardRef.current;
    if (!scrollRegion || !stickyCard) return;
    const update = (): void => {
      scrollRegion.style.setProperty('--score-athlete-card-height', `${stickyCard.offsetHeight}px`);
    };
    update();
    const observer = new ResizeObserver(update);
    observer.observe(stickyCard);
    window.addEventListener('resize', update);
    return () => {
      observer.disconnect();
      window.removeEventListener('resize', update);
    };
  }, [currentAthlete?.id, activeGroupId]);

  // Compact-on-scroll behavior (mirrors real page).
  useEffect(() => {
    const scrollRegion = scrollRegionRef.current;
    if (!scrollRegion) return;
    const mq = window.matchMedia(MOBILE_CARD_COMPACT_MEDIA_QUERY);
    const ENTER = 32;
    const EXIT = 2;
    const handleScroll = (): void => {
      if (!mq.matches) { setCardCompact(false); return; }
      if (!activeGroupId) { setCardCompact(false); return; }
      const top = scrollRegion.scrollTop;
      setCardCompact((prev) => {
        if (!prev && top > ENTER) return true;
        if (prev && top <= EXIT) return false;
        return prev;
      });
    };
    handleScroll();
    scrollRegion.addEventListener('scroll', handleScroll, { passive: true });
    mq.addEventListener('change', handleScroll);
    return () => {
      scrollRegion.removeEventListener('scroll', handleScroll);
      mq.removeEventListener('change', handleScroll);
    };
  }, [currentAthlete?.id, activeGroupId]);

  useEffect(() => { setCardViewerOpen(false); }, [currentAthlete?.id]);

  if (!currentAthlete) {
    return (
      <div className="score-athlete-shell score-athlete-shell--center">
        <p>Athlete not found.</p>
        <Button onClick={() => navigate('/demo/tryout/athletes')}>Back to Athletes</Button>
      </div>
    );
  }

  const cardImageSrc = currentAthlete.imageUrl || currentAthlete.imageThumbnailUrl || null;
  const cardCompactForUi = Boolean(activeGroupId) && cardCompact;
  const activeGroup = groups.find((g) => g.id === activeGroupId) ?? null;

  const toggleQuickAccess = (): void => {
    const next = inQuickAccess
      ? tabbedAthletes.filter((id) => id !== currentAthlete.id)
      : [...tabbedAthletes, currentAthlete.id];
    setTabbedAthletes(next);
  };

  const handleScoreChange = (field: FormField, value: SkillValue): void => {
    setScoreField(currentAthlete.id, field.id, value);
  };

  const renderSkillField = (field: FormField): React.ReactNode => {
    const fieldValue = parseSkillFieldValue(currentAthlete.scores[field.id]);
    return (
      <DemoSkillField
        field={field}
        fieldValue={fieldValue}
        subjectId={currentAthlete.id}
        currentCoachUid={coach.uid}
        currentCoachName={coach.name}
        onChange={(next) => handleScoreChange(field, next)}
      />
    );
  };

  return (
    <div className="score-athlete-shell">
      <PageTopNav
        onBack={() => navigate('/demo/tryout/athletes')}
        backAriaLabel="Back to athlete list"
        title={tryoutMeta.name}
      />

      <div ref={scrollRegionRef} className="score-athlete-scroll-region">
        <section
          ref={stickyCardRef}
          className={`score-athlete-card${cardCompactForUi ? ' score-athlete-card--compact' : ''}`}
        >
          <AthleteCardTop
            name={currentAthlete.name}
            number={currentAthlete.number}
            position={currentAthlete.position}
            returningstatus={currentAthlete.returningstatus}
            imageSrc={cardImageSrc}
            compact={cardCompactForUi}
            onPhotoClick={() => setCardViewerOpen(true)}
            rightSlot={(
              <div className="score-athlete-card-actions">
                <button
                  type="button"
                  className={`score-athlete-quick-access-btn${inQuickAccess ? ' score-athlete-quick-access-btn--active' : ''}`}
                  onClick={toggleQuickAccess}
                  aria-label={inQuickAccess ? 'Remove from quick access' : 'Add to quick access'}
                >
                  {inQuickAccess ? '★' : '☆'}
                </button>
              </div>
            )}
          />
        </section>

        <main className={`score-athlete-content${hasQuickAccessHotbar ? ' score-athlete-content--with-hotbar' : ''}`}>
          {activeGroup ? (
            <section className="score-athlete-fields">
              <button
                type="button"
                className="score-athlete-section-header"
                onClick={() => setActiveGroupId('')}
                aria-label="Back to sections"
              >
                <span className="score-athlete-section-header-back-icon" aria-hidden="true">
                  <BackArrowIcon />
                </span>
                <h3 className="score-athlete-section-title">{activeGroup.label}</h3>
              </button>
              {activeGroup.id === GENERAL_COMMENTS_GROUP_ID ? (
                <PerCoachCommentsSection
                  comments={parsePerCoachComments(currentAthlete.scores[GENERAL_COMMENTS_FIELD_ID])}
                  currentCoachUid={coach.uid}
                  currentCoachName={coach.name}
                  onAddComment={async (text) => {
                    addGeneralComment(currentAthlete.id, {
                      id: generateCommentId(),
                      text,
                      coachName: coach.name,
                      coachUid: coach.uid,
                    });
                  }}
                  onEditComment={async (commentId, newText) => {
                    editGeneralComment(currentAthlete.id, commentId, newText);
                  }}
                  onDeleteComment={async (commentId) => {
                    deleteGeneralComment(currentAthlete.id, commentId);
                  }}
                />
              ) : (
                <div className="score-athlete-fields-list">
                  {[...activeGroup.fields]
                    .sort((a, b) => a.order - b.order)
                    .map((field) => (
                      <div key={field.id}>{renderSkillField(field)}</div>
                    ))}
                </div>
              )}
            </section>
          ) : (
            <section className="score-athlete-section-launcher">
              {groups.length === 0 ? (
                <p>No scoring sections configured for this tryout.</p>
              ) : (
                groups.map((group: FormGroup) => {
                  if (group.id === GENERAL_COMMENTS_GROUP_ID) {
                    const commentCount = parsePerCoachComments(
                      currentAthlete.scores[GENERAL_COMMENTS_FIELD_ID],
                    ).length;
                    return (
                      <SectionLauncherButton
                        key={group.id}
                        label={group.label}
                        scoredCount={commentCount}
                        scorableCount={commentCount}
                        isGeneralComments
                        onClick={() => setActiveGroupId(group.id)}
                      />
                    );
                  }
                  const scorable = group.fields.filter((f) => f.type !== 'heading');
                  const scored = scorable.filter((f) => {
                    const val = currentAthlete.scores[f.id];
                    if (!val) return false;
                    const parsed = parseSkillFieldValue(val);
                    return (
                      (typeof parsed.rating === 'number' && parsed.rating > 0)
                      || (parsed.comments?.length ?? 0) > 0
                      || parsed.photo !== undefined
                      || parsed.video !== undefined
                    );
                  });
                  return (
                    <SectionLauncherButton
                      key={group.id}
                      label={group.label}
                      scoredCount={scored.length}
                      scorableCount={scorable.length}
                      onClick={() => setActiveGroupId(group.id)}
                    />
                  );
                })
              )}
            </section>
          )}
        </main>
      </div>

      {hasQuickAccessHotbar ? (
        <div className="athlete-quick-select-hotbar">
          <AthleteQuickSelectTabBar
            athletes={quickAccessAthletes}
            currentAthleteId={currentAthlete.id}
            onAthleteSelect={(id) => navigate(`/demo/tryout/athletes/${id}`)}
          />
        </div>
      ) : null}

      {cardImageSrc ? (
        <FullscreenPhotoViewer
          open={cardViewerOpen}
          src={cardImageSrc}
          alt={currentAthlete.name}
          viewerLabel={`${currentAthlete.name} photo preview`}
          onClose={() => setCardViewerOpen(false)}
        />
      ) : null}
    </div>
  );
};

export default DemoScoreAthletePage;
